import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import { Button, Modal, FormField, ListPageHeader } from '../components/ui';
import { CardTabs } from '../components/card';
import NumberInput from '../components/NumberInput';
import { toast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';

const ACTION_TYPES = [
  { v: 'pct', label: 'Yüzde indirim (kademeli %)' },
  { v: 'amount', label: 'Tutar indirim (birim başına TL)' },
  { v: 'fixed_price', label: 'Sabit birim fiyat (TL)' },
];
const CHANNELS = [
  { v: '', label: 'Tüm kanallar' },
  { v: 'retail', label: 'Perakende (PoS)' },
  { v: 'wholesale', label: 'Toptan' },
  { v: 'ecommerce', label: 'e-Ticaret' },
];
const DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const emptyCampaign = {
  code: '', name: '', campaign_kind: 'discount', action_type: 'pct',
  pct1: '', pct2: '', pct3: '', pct4: '', pct5: '',
  amount_value: '',
  scope_channel: '', applies_to: 'product',
  min_qty: '', priority: 100, stackable: false,
  valid_from: '', valid_to: '', days_mask: null,
  max_usage: '', is_active: true,
  targets: [],
  // sepet (basket)
  basket_mode: 'recurring', threshold_base: 'order',
  min_amount: '', step_amount: '', step_reward: '', reward_type: 'amount', max_discount: '',
  tiers: [],
};

const KINDS = [
  { v: 'discount', label: 'Ürün indirimi (satır bazlı)' },
  { v: 'basket', label: 'Sepet kampanyası (tutar eşiği)' },
];
const BASKET_MODES = [
  { v: 'recurring', label: 'Tekrarlı — her X TL\'ye Y indirim' },
  { v: 'tiered', label: 'Kademeli — eşik arttıkça artan indirim' },
  { v: 'once', label: 'Tek eşik — X TL üzeri sabit indirim' },
];

// %50+%20 → efektif birleşik indirim
function effectivePct(rates) {
  let f = 1;
  rates.forEach(r => { const v = parseFloat(r) || 0; if (v > 0) f *= (1 - v / 100); });
  return +((1 - f) * 100).toFixed(2);
}

export default function CampaignsPage({ onNavigate }) {
  const { hasPermission } = useAuth();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyCampaign });
  const [isEditing, setIsEditing] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showModal, setShowModal] = useState(false);
  // ürün arama
  const [prodSearch, setProdSearch] = useState('');
  const [prodResults, setProdResults] = useState([]);

  const canEdit = hasPermission('campaigns.edit') || hasPermission('campaigns.create');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/campaigns', { params: { search, _t: Date.now() } });
      if (res.data.success) {
        setList(res.data.data?.items || []);
        setTotal(res.data.data?.total ?? 0);
      }
    } catch (err) { console.error(err); setList([]); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSelect = async (c) => {
    try {
      const res = await api.get('/campaigns/' + c.id);
      if (res.data.success) {
        const d = res.data.data;
        setSelected(d);
        setForm({ ...emptyCampaign, ...d, targets: d.targets || [], tiers: (d.tiers || []).map(t => ({ min_amount: t.min_amount, reward_type: t.reward_type, reward_value: t.reward_value })) });
        setIsEditing(false); setIsNew(false); setActiveTab('general'); setMsg(null); setShowModal(true);
      }
    } catch (err) { console.error(err); }
  };

  const handleNew = () => {
    setSelected(null);
    setForm({ ...emptyCampaign });
    setIsEditing(true); setIsNew(true); setActiveTab('general'); setMsg(null); setShowModal(true);
  };

  const searchProducts = async (q) => {
    setProdSearch(q);
    if (!q || q.length < 2) { setProdResults([]); return; }
    try {
      const r = await api.get('/stocks', { params: { search: q, limit: 10, is_active: 'yes' } });
      if (r.data.success) setProdResults(r.data.data?.items || r.data.data || []);
    } catch (_) { setProdResults([]); }
  };
  const addTarget = (p) => {
    if (form.targets.some(t => String(t.stock_id) === String(p.id))) return;
    f('targets', [...form.targets, { stock_id: p.id, stock_code: p.code, stock_name: p.name }]);
    setProdSearch(''); setProdResults([]);
  };
  const removeTarget = (sid) => f('targets', form.targets.filter(t => String(t.stock_id) !== String(sid)));

  const handleSave = async () => {
    if (!form.name) { setMsg({ type: 'error', text: 'Kampanya adı zorunlu' }); return; }
    const isBasket = form.campaign_kind === 'basket';
    if (!isBasket && form.applies_to === 'product' && (!form.targets || !form.targets.length)) {
      setMsg({ type: 'error', text: 'Seçili ürünler modunda en az bir ürün eklemelisiniz' }); return;
    }
    if (isBasket && form.basket_mode === 'recurring' && (!form.step_amount || !form.step_reward)) {
      setMsg({ type: 'error', text: 'Tekrarlı modda "her X TL" ve "Y indirim" zorunlu' }); return;
    }
    if (isBasket && form.basket_mode === 'once' && (!form.min_amount || !form.step_reward)) {
      setMsg({ type: 'error', text: 'Tek eşik modda "eşik tutarı" ve "indirim" zorunlu' }); return;
    }
    if (isBasket && form.basket_mode === 'tiered' && (!form.tiers || form.tiers.filter(t => t.min_amount && t.reward_value).length < 1)) {
      setMsg({ type: 'error', text: 'Kademeli modda en az bir kademe girmelisiniz' }); return;
    }
    const num = v => (v === '' || v == null) ? null : parseFloat(v);
    const payload = {
      ...form,
      pct1: num(form.pct1), pct2: num(form.pct2), pct3: num(form.pct3), pct4: num(form.pct4), pct5: num(form.pct5),
      amount_value: num(form.amount_value),
      min_qty: num(form.min_qty),
      max_usage: form.max_usage === '' ? null : parseInt(form.max_usage, 10),
      min_amount: num(form.min_amount), step_amount: num(form.step_amount), step_reward: num(form.step_reward),
      max_discount: num(form.max_discount),
      basket_mode: isBasket ? form.basket_mode : null,
      threshold_base: isBasket ? (form.threshold_base || 'order') : null,
      reward_type: isBasket ? (form.reward_type || 'amount') : null,
      valid_from: form.valid_from || null,
      valid_to: form.valid_to || null,
      targets: (!isBasket && form.applies_to === 'product') ? form.targets : [],
      tiers: (isBasket && form.basket_mode === 'tiered') ? form.tiers.filter(t => t.min_amount && t.reward_value).map(t => ({ min_amount: parseFloat(t.min_amount), reward_type: t.reward_type || 'pct', reward_value: parseFloat(t.reward_value) })) : [],
    };
    setSaving(true); setMsg(null);
    try {
      if (isNew) {
        const r = await api.post('/campaigns', payload);
        if (r.data.success) { toast.success('Kampanya oluşturuldu'); await fetchList(); setShowModal(false); }
      } else {
        await api.put('/campaigns/' + selected.id, payload);
        toast.success('Kampanya güncellendi'); await fetchList(); setIsEditing(false);
        setSelected({ ...selected, ...payload });
      }
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Kayıt başarısız' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm(`"${selected.name}" kampanyasını silmek (pasifleştirmek) istiyor musunuz?`)) return;
    try {
      await api.delete('/campaigns/' + selected.id);
      toast.success('Kampanya pasifleştirildi'); setShowModal(false); fetchList();
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Silme başarısız' }); }
  };

  const describeAction = (c) => {
    if (c.campaign_kind === 'basket') {
      const cap = c.max_discount ? ` (max ${+c.max_discount} TL)` : '';
      if (c.basket_mode === 'recurring') return `Her ${+c.step_amount || 0} TL'ye ${c.reward_type === 'pct' ? '%' : ''}${+c.step_reward || 0}${c.reward_type === 'pct' ? '' : ' TL'}${cap}`;
      if (c.basket_mode === 'once') return `${+c.min_amount || 0} TL üzeri ${c.reward_type === 'pct' ? '%' : ''}${+c.step_reward || 0}${c.reward_type === 'pct' ? '' : ' TL'}${cap}`;
      if (c.basket_mode === 'tiered') return `Kademeli sepet indirimi${cap}`;
      return 'Sepet kampanyası';
    }
    if (c.action_type === 'pct') {
      const rates = [c.pct1, c.pct2, c.pct3, c.pct4, c.pct5].filter(x => parseFloat(x) > 0).map(x => '%' + (+x));
      return rates.length ? rates.join(' + ') + ` (efektif %${effectivePct([c.pct1,c.pct2,c.pct3,c.pct4,c.pct5])})` : '—';
    }
    if (c.action_type === 'amount') return `−${(+c.amount_value || 0)} TL/adet`;
    if (c.action_type === 'fixed_price') return `Sabit ${(+c.amount_value || 0)} TL`;
    return '—';
  };

  const columns = [
    { id: 'name', header: 'Kampanya', accessor: 'name', sortable: true },
    { id: 'action', header: 'İndirim', accessor: c => describeAction(c), width: 220 },
    { id: 'applies', header: 'Kapsam', accessor: c => c.applies_to === 'all' ? 'Tüm ürünler' : (c.applies_to === 'category' ? 'Kategori' : `${c.target_count || 0} ürün`), width: 110 },
    { id: 'channel', header: 'Kanal', accessor: c => (CHANNELS.find(x => x.v === (c.scope_channel || ''))?.label) || '—', width: 130 },
    { id: 'dates', header: 'Geçerlilik', accessor: c => `${c.valid_from ? String(c.valid_from).slice(0,10) : '∞'} → ${c.valid_to ? String(c.valid_to).slice(0,10) : '∞'}`, width: 190 },
    { id: 'status', header: 'Durum', accessor: c => c.is_active ? '✅ Aktif' : '⏸ Pasif', width: 90 },
  ];

  const tabs = [
    { key: 'general', label: 'Genel' },
    { key: 'discount', label: 'İndirim' },
    { key: 'scope', label: 'Kapsam & Koşul' },
  ];

  const ro = !isEditing; // read-only

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Kampanyalar"
        count={total}
        unit="kampanya"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Kampanya adı ara..."
        onRefresh={fetchList}
        refreshing={loading}
        actions={hasPermission('campaigns.create') ? <Button size="sm" onClick={handleNew}>+ Yeni Kampanya</Button> : null}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="campaigns-list"
          tableClassName="stocks-table"
          data={list}
          rowKey="id"
          columns={columns}
          loading={loading}
          emptyText="Henüz kampanya yok"
          onRowClick={c => handleSelect(c)}
        />
      </div>

      {showModal && (
        <Modal
          open={showModal}
          onClose={() => { setShowModal(false); setSelected(null); }}
          title={isNew ? 'Yeni Kampanya' : (selected?.name || 'Kampanya')}
          width={760}
          footer={
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <div>
                {!isNew && !isEditing && hasPermission('campaigns.delete') && (
                  <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {isEditing ? (
                  <>
                    {!isNew && <Button variant="ghost" onClick={() => { setForm({ ...emptyCampaign, ...selected, targets: selected.targets || [] }); setIsEditing(false); setMsg(null); }}>Vazgeç</Button>}
                    <Button onClick={handleSave} disabled={saving}>{saving ? 'Kaydediliyor…' : '💾 Kaydet'}</Button>
                  </>
                ) : (
                  canEdit && <Button onClick={() => setIsEditing(true)}>✎ Düzenle</Button>
                )}
              </div>
            </div>
          }
        >
          {msg && (
            <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: 13,
              background: msg.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              color: msg.type === 'success' ? '#16a34a' : '#dc2626' }}>{msg.text}</div>
          )}

          <CardTabs items={tabs} active={activeTab} onChange={setActiveTab} />

          {activeTab === 'general' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 12 }}>
              <FormField label="Kampanya Türü" hint="Ürün = satıra; Sepet = toplam tutara">
                <select value={form.campaign_kind} onChange={e => f('campaign_kind', e.target.value)} disabled={ro}>
                  {KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
                </select>
              </FormField>
              <FormField label="Kampanya Adı" required>
                <input value={form.name || ''} onChange={e => f('name', e.target.value)} disabled={ro} placeholder="Bahar Kampanyası" />
              </FormField>
              <FormField label="Kod (opsiyonel)">
                <input value={form.code || ''} onChange={e => f('code', e.target.value)} disabled={ro} placeholder="BAHAR26" />
              </FormField>
              <FormField label="Öncelik" hint="Yüksek olan önce uygulanır">
                <input type="number" value={form.priority} onChange={e => f('priority', e.target.value)} disabled={ro} />
              </FormField>
              <FormField label="Durum">
                <select value={form.is_active ? '1' : '0'} onChange={e => f('is_active', e.target.value === '1')} disabled={ro}>
                  <option value="1">Aktif</option><option value="0">Pasif</option>
                </select>
              </FormField>
            </div>
          )}

          {activeTab === 'discount' && form.campaign_kind === 'discount' && (
            <div>
              <FormField label="İndirim Tipi">
                <select value={form.action_type} onChange={e => f('action_type', e.target.value)} disabled={ro}>
                  {ACTION_TYPES.map(a => <option key={a.v} value={a.v}>{a.label}</option>)}
                </select>
              </FormField>

              {form.action_type === 'pct' && (
                <>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0' }}>
                    Kademeli yüzde — örn. %50 + %20 ardışık uygulanır.
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8 }}>
                    {[1,2,3,4,5].map(n => (
                      <FormField key={n} label={`%${n}. kademe`}>
                        <NumberInput value={form['pct'+n]} onChange={v => f('pct'+n, v)} decimals={2} disabled={ro} />
                      </FormField>
                    ))}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: '#16a34a' }}>
                    Efektif indirim: %{effectivePct([form.pct1,form.pct2,form.pct3,form.pct4,form.pct5])}
                  </div>
                </>
              )}
              {form.action_type === 'amount' && (
                <FormField label="Birim başına indirim (TL)">
                  <NumberInput value={form.amount_value} onChange={v => f('amount_value', v)} decimals={2} disabled={ro} />
                </FormField>
              )}
              {form.action_type === 'fixed_price' && (
                <FormField label="Sabit birim fiyat (TL)">
                  <NumberInput value={form.amount_value} onChange={v => f('amount_value', v)} decimals={2} disabled={ro} />
                </FormField>
              )}
            </div>
          )}

          {activeTab === 'discount' && form.campaign_kind === 'basket' && (
            <div style={{ display: 'grid', gap: 12 }}>
              <FormField label="Sepet kampanya modu">
                <select value={form.basket_mode} onChange={e => f('basket_mode', e.target.value)} disabled={ro}>
                  {BASKET_MODES.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
                </select>
              </FormField>

              {form.basket_mode === 'recurring' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
                  <FormField label="Her (TL)" hint="örn. 5000">
                    <NumberInput value={form.step_amount} onChange={v => f('step_amount', v)} decimals={2} disabled={ro} />
                  </FormField>
                  <FormField label="İndirim">
                    <NumberInput value={form.step_reward} onChange={v => f('step_reward', v)} decimals={2} disabled={ro} />
                  </FormField>
                  <FormField label="İndirim tipi">
                    <select value={form.reward_type} onChange={e => f('reward_type', e.target.value)} disabled={ro}>
                      <option value="amount">TL (tutar)</option><option value="pct">% (yüzde)</option>
                    </select>
                  </FormField>
                  <div style={{ alignSelf: 'end', fontSize: 12.5, color: '#16a34a' }}>
                    Örn: her {(+form.step_amount || 0)} TL'ye {(+form.step_reward || 0)}{form.reward_type === 'pct' ? '%' : ' TL'} indirim
                  </div>
                </div>
              )}

              {form.basket_mode === 'once' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
                  <FormField label="Eşik tutarı (TL)" hint="bu tutar üzeri">
                    <NumberInput value={form.min_amount} onChange={v => f('min_amount', v)} decimals={2} disabled={ro} />
                  </FormField>
                  <FormField label="İndirim">
                    <NumberInput value={form.step_reward} onChange={v => f('step_reward', v)} decimals={2} disabled={ro} />
                  </FormField>
                  <FormField label="İndirim tipi">
                    <select value={form.reward_type} onChange={e => f('reward_type', e.target.value)} disabled={ro}>
                      <option value="amount">TL (tutar)</option><option value="pct">% (yüzde)</option>
                    </select>
                  </FormField>
                </div>
              )}

              {form.basket_mode === 'tiered' && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                    Kademeler — ulaşılan en yüksek kademe uygulanır. (örn. 1000→%10, 2000→%20)
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: 12 }}>
                      <th style={{ padding: 6 }}>Eşik tutar (TL)</th><th style={{ padding: 6, width: 110 }}>Tip</th><th style={{ padding: 6, width: 120 }}>İndirim</th><th style={{ width: 40 }}></th>
                    </tr></thead>
                    <tbody>
                      {(form.tiers || []).map((t, i) => (
                        <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={{ padding: 6 }}><NumberInput value={t.min_amount} onChange={v => f('tiers', form.tiers.map((x, j) => j === i ? { ...x, min_amount: v } : x))} decimals={2} disabled={ro} /></td>
                          <td style={{ padding: 6 }}>
                            <select value={t.reward_type || 'pct'} onChange={e => f('tiers', form.tiers.map((x, j) => j === i ? { ...x, reward_type: e.target.value } : x))} disabled={ro}>
                              <option value="pct">%</option><option value="amount">TL</option>
                            </select>
                          </td>
                          <td style={{ padding: 6 }}><NumberInput value={t.reward_value} onChange={v => f('tiers', form.tiers.map((x, j) => j === i ? { ...x, reward_value: v } : x))} decimals={2} disabled={ro} /></td>
                          <td style={{ padding: 6 }}>{!ro && <span onClick={() => f('tiers', form.tiers.filter((_, j) => j !== i))} style={{ cursor: 'pointer', color: '#dc2626' }}>✕</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!ro && <Button variant="ghost" size="sm" onClick={() => f('tiers', [...(form.tiers || []), { min_amount: '', reward_type: 'pct', reward_value: '' }])}>+ Kademe ekle</Button>}
                </div>
              )}

              <FormField label="Maksimum indirim / kota (TL)" hint="Boş = sınırsız; marj koruması için tavan">
                <NumberInput value={form.max_discount} onChange={v => f('max_discount', v)} decimals={2} disabled={ro} />
              </FormField>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                Sepet indirimi, faturadaki kalemlere tutarlarına göre orantılı dağıtılır (KDV korunur).
              </div>
            </div>
          )}

          {activeTab === 'scope' && (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12 }}>
                <FormField label="Satış kanalı">
                  <select value={form.scope_channel || ''} onChange={e => f('scope_channel', e.target.value)} disabled={ro}>
                    {CHANNELS.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
                  </select>
                </FormField>
                <FormField label="Min. adet (eşik)" hint="X adet ve üzeri">
                  <NumberInput value={form.min_qty} onChange={v => f('min_qty', v)} decimals={0} disabled={ro} />
                </FormField>
                <FormField label="Başlangıç tarihi">
                  <input type="date" value={form.valid_from ? String(form.valid_from).slice(0,10) : ''} onChange={e => f('valid_from', e.target.value)} disabled={ro} />
                </FormField>
                <FormField label="Bitiş tarihi">
                  <input type="date" value={form.valid_to ? String(form.valid_to).slice(0,10) : ''} onChange={e => f('valid_to', e.target.value)} disabled={ro} />
                </FormField>
                <FormField label="Max kullanım (opsiyonel)">
                  <input type="number" value={form.max_usage || ''} onChange={e => f('max_usage', e.target.value)} disabled={ro} />
                </FormField>
                <FormField label="Üst üste binebilir">
                  <select value={form.stackable ? '1' : '0'} onChange={e => f('stackable', e.target.value === '1')} disabled={ro}>
                    <option value="0">Hayır (tek kampanya)</option><option value="1">Evet</option>
                  </select>
                </FormField>
              </div>

              <FormField label="Geçerli günler" hint="Boş = her gün">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {DAYS.map((d, i) => {
                    const bit = 1 << i;
                    const on = form.days_mask != null && (form.days_mask & bit) !== 0;
                    return (
                      <button key={d} type="button" disabled={ro}
                        onClick={() => { const cur = form.days_mask || 0; const nx = on ? (cur & ~bit) : (cur | bit); f('days_mask', nx === 0 ? null : nx); }}
                        style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: ro ? 'default' : 'pointer',
                          border: '1px solid var(--border)', background: on ? 'var(--primary)' : 'transparent', color: on ? '#fff' : 'var(--text)' }}>
                        {d}
                      </button>
                    );
                  })}
                </div>
              </FormField>

              <FormField label="Hangi ürünlere uygulanır?">
                <select value={form.applies_to} onChange={e => f('applies_to', e.target.value)} disabled={ro}>
                  <option value="product">Seçili ürünler</option>
                  <option value="all">Tüm ürünler</option>
                </select>
              </FormField>

              {form.applies_to === 'product' && (
                <div>
                  {!ro && (
                    <div style={{ position: 'relative', marginBottom: 8 }}>
                      <input value={prodSearch} onChange={e => searchProducts(e.target.value)} placeholder="Ürün ara (kod/ad/barkod)…" />
                      {prodResults.length > 0 && (
                        <div style={{ position: 'absolute', zIndex: 20, background: 'var(--card-bg,#fff)', border: '1px solid var(--border)', borderRadius: 8, width: '100%', maxHeight: 220, overflow: 'auto', boxShadow: '0 6px 20px rgba(0,0,0,.15)' }}>
                          {prodResults.map(p => (
                            <div key={p.id} onClick={() => addTarget(p)} style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                              <b>{p.code}</b> — {p.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(form.targets || []).map(t => (
                      <span key={t.stock_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 6, background: 'rgba(59,130,246,.1)', fontSize: 12 }}>
                        {t.stock_code || ''} {t.stock_name || ''}
                        {!ro && <span onClick={() => removeTarget(t.stock_id)} style={{ cursor: 'pointer', color: '#dc2626' }}>✕</span>}
                      </span>
                    ))}
                    {(!form.targets || !form.targets.length) && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Henüz ürün eklenmedi</span>}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
